// src/pages/reportes/components/ReportesGuardados.tsx
import { useState } from 'react';
import { Card, Button, Spinner, Badge, toast } from '@/components/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  FileText,
  Download,
  Trash2,
  AlertCircle,
  FileSpreadsheet,
  FileCode,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useReportesGuardados, useExportarReporte, useEliminarReporteGuardado } from '@/hooks/queries/useReportes';
import { TipoReporte } from '@/types/reporte';

const TIPO_REPORTE_LABELS: Record<TipoReporte, string> = {
  [TipoReporte.DASHBOARD]: 'Dashboard',
  [TipoReporte.OPERATIVO]: 'Operativo',
  [TipoReporte.GERENCIAL]: 'Gerencial',
  [TipoReporte.CLIENTE]: 'Cliente',
  [TipoReporte.EXPORTABLE]: 'Exportable',
};

export function ReportesGuardados() {
  const [filtroTipo, setFiltroTipo] = useState<TipoReporte | ''>('');
  const { data: reportes, isLoading, error } = useReportesGuardados(filtroTipo || undefined);
  const exportarMutation = useExportarReporte();
  const eliminarMutation = useEliminarReporteGuardado();

  const handleExportar = async (reporteId: string, formato: 'pdf' | 'xlsx' | 'csv') => {
    try {
      const blob = await exportarMutation.mutateAsync({
        reporteId,
        data: { formato, incluir_graficos: formato === 'pdf' },
      });

      // Crear link de descarga
      const url = window.URL.createObjectURL(blob as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte.${formato}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(`Reporte exportado como ${formato.toUpperCase()}`);
    } catch {
      // Error handled by global MutationCache.onError
    }
  };

  const handleEliminar = async (reporteId: string) => {
    if (!window.confirm('Estas seguro de eliminar este reporte?')) return;

    try {
      await eliminarMutation.mutateAsync(reporteId);
      toast.success('Reporte eliminado correctamente');
    } catch {
      // Error handled by global MutationCache.onError
    }
  };

  const formatFecha = (fecha: string) => {
    try {
      return format(new Date(fecha), 'dd MMM yyyy HH:mm', { locale: es });
    } catch {
      return fecha;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="text-center text-red-500">
          <AlertCircle className="h-12 w-12 mx-auto mb-4" />
          <p>Error al cargar los reportes guardados</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filtro por tipo */}
      <Card className="p-4">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-gray-700">Filtrar por tipo:</span>
          <Select value={filtroTipo} onValueChange={(v) => setFiltroTipo(v as TipoReporte | '')}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Todos los tipos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">Todos los tipos</SelectItem>
              {Object.entries(TIPO_REPORTE_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Lista de reportes */}
      <Card>
        {!reportes || reportes.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>No hay reportes guardados</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Nombre</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Tipo</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Periodo</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Generado</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-700">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {reportes.map((reporte) => (
                  <tr key={reporte.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-400" />
                        <span className="font-medium">{reporte.nombre}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant="default">
                        {TIPO_REPORTE_LABELS[reporte.tipo] || reporte.tipo}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {formatFecha(reporte.fecha_inicio)} - {formatFecha(reporte.fecha_fin)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {formatFecha(reporte.generado_at)}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-1">
                        {/* Exportar PDF */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleExportar(reporte.id, 'pdf')}
                          disabled={exportarMutation.isPending}
                          title="Exportar PDF"
                        >
                          <Download className="h-4 w-4 text-red-600" />
                        </Button>
                        {/* Exportar Excel */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleExportar(reporte.id, 'xlsx')}
                          disabled={exportarMutation.isPending}
                          title="Exportar Excel"
                        >
                          <FileSpreadsheet className="h-4 w-4 text-green-600" />
                        </Button>
                        {/* Exportar CSV */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleExportar(reporte.id, 'csv')}
                          disabled={exportarMutation.isPending}
                          title="Exportar CSV"
                        >
                          <FileCode className="h-4 w-4 text-blue-600" />
                        </Button>
                        {/* Eliminar */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEliminar(reporte.id)}
                          disabled={eliminarMutation.isPending}
                          title="Eliminar"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
